import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Form, Button, Image,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import _ from 'lodash';
import Error from '../../../Components/Error';
import FormControlFeedback from '../../../Components/FormControlFeedback';
import setStateFromInput from '../../../Utils/setStateFromInput';
import ExperienceGroupService from '../../../Services/ExperienceGroupService';

const DEFAULT_CATEGORIES = [
  'Walking Tour',
  'Wine Tour',
  'Day Trip',
];

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hu', label: 'Hungarian' },
  { value: 'ru', label: 'Russian' },
];

function ExperienceGroupInputModal({
  originalGroup = {},
  show = false,
  handleClose,
  fetchGroups,
}) {
  const defaultGroup = {
    active: 1,
    title: '',
    description: '',
    category: '',
    link: '',
    featured: 0,
    sort_order: 0,
    tags: [],
    language: 'en',
  };

  const [group, setGroup] = useState(
    _.defaults({ ...originalGroup }, defaultGroup),
  );
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Fetch existing categories from experience groups
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await ExperienceGroupService.fetchExperienceGroups();
        const existingCategories = response.data?.data
          ?.map((g) => g.category)
          .filter(Boolean) || [];
        const allCategories = [...new Set([...DEFAULT_CATEGORIES, ...existingCategories])];
        setCategories(allCategories.sort());
      } catch (err) {
        setCategories(DEFAULT_CATEGORIES);
      }
    };
    if (show) {
      fetchCategories();
    }
  }, [show]);

  // Handle image file selection
  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only PNG, JPEG, and WebP images are allowed');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.split(',')[1];

      // If group already exists, upload immediately
      if (group.Id) {
        setImageUploading(true);
        try {
          const result = await ExperienceGroupService.uploadExperienceGroupImage(
            group.Id,
            base64,
            file.type,
          );
          setGroup((prev) => ({
            ...prev,
            image_key: result.data?.data?.image_key,
            image_url: result.data?.data?.image_url,
          }));
        } catch (err) {
          setError(err.response?.data?.message || 'Failed to upload image');
        } finally {
          setImageUploading(false);
        }
      } else {
        // For new groups, store the pending image data
        setGroup((prev) => ({
          ...prev,
          pendingImage: { base64, contentType: file.type },
          imagePreview: reader.result,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  // Remove image
  const handleRemoveImage = async () => {
    if (group.Id && group.image_key) {
      setImageUploading(true);
      try {
        await ExperienceGroupService.removeExperienceGroupImage(group.Id);
        setGroup((prev) => ({
          ...prev,
          image_key: undefined,
          image_url: undefined,
        }));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to remove image');
      } finally {
        setImageUploading(false);
      }
    } else {
      // Clear pending image
      setGroup((prev) => ({
        ...prev,
        pendingImage: undefined,
        imagePreview: undefined,
      }));
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const payload = {
        ...group,
        active: Number(group.active),
        featured: Number(group.featured),
        sort_order: Number(group.sort_order) || 0,
      };

      // Remove UI-only fields
      delete payload.pendingImage;
      delete payload.imagePreview;
      delete payload.image_url;

      const result = group.Id
        ? await ExperienceGroupService.updateExperienceGroup(payload)
        : await ExperienceGroupService.createExperienceGroup(payload);

      const savedGroup = result.data?.data;

      // If this was a new group and we have a pending image, upload it now
      if (!group.Id && group.pendingImage && savedGroup?.Id) {
        try {
          await ExperienceGroupService.uploadExperienceGroupImage(
            savedGroup.Id,
            group.pendingImage.base64,
            group.pendingImage.contentType,
          );
        } catch (imgErr) {
          // eslint-disable-next-line no-console
          console.error('Failed to upload image:', imgErr);
        }
      }

      fetchGroups();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save experience group');

      if (err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setStateFromInput(e, setGroup, group);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {group.Id ? (
            <>
              Edit Group:
              {' '}
              {group.title}
            </>
          ) : (
            <>New Experience Group</>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        <Form>
          <div className="row">
            <Form.Group className="mb-3 col-6" controlId="title">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                value={group.title}
                onChange={handleChange}
                placeholder="e.g., Wine Tours"
              />
              <FormControlFeedback errors={validationErrors} field="title" />
            </Form.Group>

            <Form.Group className="mb-3 col-4" controlId="category">
              <Form.Label>Category</Form.Label>
              <Typeahead
                id="category_typeahead"
                allowNew
                newSelectionPrefix="Create: "
                placeholder="Select or create..."
                options={categories}
                selected={group.category ? [group.category] : []}
                onChange={(selected) => {
                  const value = selected[0];
                  const categoryValue = typeof value === 'string'
                    ? value
                    : value?.label || '';
                  setGroup((prev) => ({ ...prev, category: categoryValue }));
                }}
              />
              <FormControlFeedback errors={validationErrors} field="category" />
            </Form.Group>

            <Form.Group className="mb-3 col-2" controlId="sort_order">
              <Form.Label>Order</Form.Label>
              <Form.Control
                type="number"
                min={0}
                value={group.sort_order || 0}
                onChange={handleChange}
              />
            </Form.Group>
          </div>

          <Form.Group className="mb-3" controlId="description">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={group.description || ''}
              onChange={handleChange}
              placeholder="Describe the experience group..."
            />
            <FormControlFeedback errors={validationErrors} field="description" />
          </Form.Group>

          <Form.Group className="mb-3" controlId="link">
            <Form.Label>URL Path (optional)</Form.Label>
            <Form.Control
              type="text"
              value={group.link || ''}
              onChange={handleChange}
              placeholder="/tours/wine-tours"
            />
            <Form.Text className="text-muted">
              URL path for the group page on the website
            </Form.Text>
          </Form.Group>

          {/* Tags */}
          <Form.Group className="mb-3" controlId="tags">
            <Form.Label>Public Tags (comma-separated)</Form.Label>
            <Form.Control
              type="text"
              value={(group.tags || []).join(', ')}
              onChange={(e) => {
                const tags = e.target.value
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean);
                setGroup((prev) => ({ ...prev, tags }));
              }}
              placeholder="e.g. wine-tours-of-hungary, pickup-hungary"
            />
            <Form.Text className="text-muted">
              Controls which sites can fetch this group publicly. All experiences in this group
              inherit these tags. E.g.
              {' '}
              <code>wine-tours-of-hungary</code>
              {' '}
              shows on the wine site.
            </Form.Text>
          </Form.Group>

          {/* Image Upload */}
          <Form.Group className="mb-3">
            <Form.Label>Cover Image</Form.Label>
            <div className="d-flex align-items-start gap-3">
              {(group.image_url || group.imagePreview) && (
                <div className="position-relative">
                  <Image
                    src={group.image_url || group.imagePreview}
                    alt="Group cover"
                    thumbnail
                    style={{ maxWidth: '150px', maxHeight: '100px', objectFit: 'cover' }}
                  />
                  <Button
                    size="sm"
                    variant="danger"
                    className="position-absolute top-0 end-0"
                    onClick={handleRemoveImage}
                    disabled={imageUploading}
                    style={{ transform: 'translate(50%, -50%)' }}
                  >
                    <i className="fa fa-times" />
                  </Button>
                </div>
              )}
              <div>
                <Form.Control
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleImageSelect}
                  ref={fileInputRef}
                  disabled={imageUploading}
                />
                <Form.Text className="text-muted">
                  PNG, JPEG, or WebP. Max 5MB.
                </Form.Text>
                {imageUploading && (
                  <div className="text-primary mt-1">
                    <i className="fa fa-spin fa-spinner me-1" />
                    Uploading...
                  </div>
                )}
              </div>
            </div>
          </Form.Group>

          <div className="row">
            <Form.Group className="mb-3 col-4" controlId="featured">
              <Form.Label>Featured</Form.Label>
              <Form.Select value={group.featured} onChange={handleChange}>
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Featured groups appear prominently on the website
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3 col-4" controlId="language">
              <Form.Label>Language</Form.Label>
              <Form.Select value={group.language || 'en'} onChange={handleChange}>
                {LANGUAGES.map((lang) => (
                  <option key={lang.value} value={lang.value}>{lang.label}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Locale this group is shown for on the public website
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3 col-4" controlId="active">
              <Form.Label>Status</Form.Label>
              <Form.Select value={group.active} onChange={handleChange}>
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </Form.Select>
            </Form.Group>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <i className="fa fa-spin fa-spinner me-2" />
              Saving...
            </>
          ) : (
            <>
              <i className="fa fa-save me-2" />
              Save Group
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default ExperienceGroupInputModal;
